import { getColorByString } from "@msuite/utils/colors";
import type { CoverageDay } from "@msuite/utils/plans";
import type { Enums } from "@msuite/utils/types";

/** What one day of a crew's bar says: staffed, short a person, or outside the crew's span. */
export type CrewBarSegment = "staffed" | "absent" | null;

const STAFFED_COLOR = "var(--color-emerald-500)";
const ABSENT_COLOR = "var(--color-amber-500)";
const COVERAGE_COLOR = "var(--color-accent-500)";
/** A crew's own colour, so two crews on one ticket are told apart without reading anything, and the
 * same crew looks the same wherever it appears. The palette name, which is what a `Tag` takes. */
export function crewColor(crewDefinitionId: string): Enums<"color"> {
	return getColorByString(crewDefinitionId);
}
/** A window is painted in its manager's own colour, so the same person reads the same on every
 * crew they hold. `getColorByString` is the palette name; the 500 step is the one a tag uses. */
export function managerSlotColor(userId: string | null): string {
	return `var(--color-${getColorByString(userId)}-500)`;
}
const OUTSIDE_COLOR = "transparent";

/**
 * A bar over the window's days, one flat band per day.
 *
 * Every stop is doubled — `<color> <from>% <to>%` — so each day is a flat band and the change
 * between two days is a hard edge rather than a blend. Adjacent days of the same colour meet
 * seamlessly, so a run reads as one block and a different day cuts a sharp section out of it.
 *
 * @param colors - One colour per day of the window, in order; null for a day the bar does not
 *   cover.
 * @returns The CSS gradient, or null when no day is covered and there is no bar to draw.
 */
export function buildHardStopGradient(colors: Array<string | null>): string | null {
	if (!colors.length) return null;

	const width = 100 / colors.length;
	const stops: string[] = [];
	let hasBlock = false;

	for (let index = 0; index < colors.length; index++) {
		const color = colors[index] ?? null;
		if (color) hasBlock = true;
		const from = (index * width).toFixed(4);
		const to = ((index + 1) * width).toFixed(4);
		stops.push(`${color ?? OUTSIDE_COLOR} ${from}% ${to}%`);
	}

	if (!hasBlock) return null;
	return `linear-gradient(to right, ${stops.join(", ")})`;
}

/**
 * The bar a crew row is painted with: emerald where the crew is staffed, amber where one of its
 * people is absent.
 *
 * @param segments - One entry per day of the window, in order.
 * @returns The CSS gradient, or null when the crew's span reaches none of these days.
 */
export function buildCrewBarGradient(segments: CrewBarSegment[]): string | null {
	return buildHardStopGradient(segments.map(colorOfSegment));
}

/**
 * The bar a manager slot is painted with — one flat colour over the days the window holds.
 *
 * It is a single colour rather than the crew's two, because a window says who may plan and nothing
 * about who is available; the crew's own bar above it already answers that. The colour is the
 * manager's, so two windows held by the same person match wherever they appear.
 *
 * @param covered - One entry per day of the window, true where the window holds that day.
 * @param color - The manager's colour, as `managerSlotColor` derives it.
 * @returns The CSS gradient, or null when the window reaches none of these days.
 */
export function buildManagerSlotBarGradient(covered: boolean[], color: string): string | null {
	return buildHardStopGradient(covered.map((isCovered) => (isCovered ? color : null)));
}

/**
 * The thin bar under a crew's own, showing which of its days some manager holds.
 *
 * One flat colour, deliberately — not the managers' own. Windows may overlap, so two adjacent
 * blocks in two colours would merge into a band that reads as a third manager, and a strip whose
 * colours cannot be trusted is worse than one that answers only the question it is for: is this
 * day covered at all. Whose window it is, the rows beneath the crew say.
 *
 * The accent rather than the amber above it: the strip sits directly under the crew's own bar, and
 * sharing that bar's absence colour would read as more of the same figure instead of a second one.
 *
 * @param covered - One entry per day of the window, true where some manager holds that day.
 * @returns The CSS gradient, or null when no day of the window is covered.
 */
export function buildManagerCoverageGradient(covered: boolean[]): string | null {
	return buildHardStopGradient(covered.map((isCovered) => (isCovered ? COVERAGE_COLOR : null)));
}

/** The colour one day of a crew's bar is painted in. */
function colorOfSegment(segment: CrewBarSegment): string | null {
	if (segment === "absent") return ABSENT_COLOR;
	if (segment === "staffed") return STAFFED_COLOR;
	return null;
}

/**
 * The bar a project or a manager row draws when its tickets are closed.
 *
 * One flat colour, the row's own — keyed off its name, so two rows are told apart down a long board
 * without reading either of them. It answers one question and no more: which days does this row
 * carry work on.
 *
 * Whether that work is crewed is **not** drawn here. `buildTicketCoverage` still reads it, and the
 * rows underneath say it plainly, but a second strength on a summary bar a few pixels tall reads as
 * a rendering fault rather than as a state.
 *
 * @param coverage - One entry per day of the window, as `buildTicketCoverage` reads it.
 * @param color - The row's own colour, as `getColorByString` derives it from its name.
 * @returns The CSS gradient, or null when no day under the row carries work.
 */
export function buildCoverageGradient(
	coverage: CoverageDay[],
	color: Enums<"color">,
): string | null {
	return buildHardStopGradient(coverage.map((day) => (day ? `var(--color-${color}-500)` : null)));
}
